/**
 * Raw OCPP frame log (Phase A.1).
 *
 * Backend: GET /api/charger/ocpp/log/:chargerId
 *
 * This is the "diagnostics under the hood" view. The friendly Activity Log
 * tab is fed by the legacy `/charger/events/...` endpoint; this tab is the
 * unfiltered OCPP wire log so on-call engineers and OCTT auditors can see
 * exactly what was sent and received.
 */

import React, { useCallback, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppJsonViewer from '../components/OcppJsonViewer';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import { fetchOcppLog } from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { formatOcppTimestamp } from '../utils/statusFormat';
import type { GetOcppLogResponse, OcppFrame, OcppFrameDirection } from '../models';
import type { OcppTabProps } from './types';

const DEFAULT_LIMIT = 200;

const directionColor = (d: OcppFrameDirection): string =>
  d === 'in' ? OcppTheme.accent : OcppTheme.info;

const RawFramesTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [frames, setFrames] = useState<OcppFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [direction, setDirection] = useState<OcppFrameDirection | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchOcppLog(chargerId, { limit: DEFAULT_LIMIT, direction });
    if (isOcppSuccess(res.status)) {
      const payload = res.payload as GetOcppLogResponse | null;
      setFrames(payload?.frames ?? []);
      setError(null);
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setLoading(false);
  }, [chargerId, direction]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <OcppLoadingState label="Loading raw OCPP frames…" />;
  if (error) return <OcppErrorBanner error={error} onRetry={load} />;

  return (
    <View>
      <View style={styles.filterRow}>
        <Text style={styles.filterLabel}>Direction</Text>
        <View style={styles.filterChips}>
          {(['all', 'in', 'out'] as const).map((opt) => {
            const active = (opt === 'all' && !direction) || direction === opt;
            return (
              <Pressable
                key={opt}
                onPress={() => setDirection(opt === 'all' ? undefined : opt)}
                accessibilityRole="button"
                style={[styles.chip, active && styles.chipActive]}
              >
                <Text style={[styles.chipText, active && styles.chipTextActive]}>
                  {opt === 'in' ? 'Charger → CSMS' : opt === 'out' ? 'CSMS → Charger' : 'All'}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {frames.length === 0 ? (
        <OcppEmptyState
          icon="speaker-notes"
          title="No frames yet"
          description="Once the charger reconnects and starts exchanging messages, the raw frames will appear here."
        />
      ) : (
        <OcppSectionCard
          title={`Frames (${frames.length})`}
          subtitle="Most recent first. Tap a row to inspect payload / response JSON."
        >
          {frames.map((frame) => {
            const expanded = expandedId === frame.id;
            return (
              <View key={frame.id} style={styles.frameRow}>
                <Pressable
                  onPress={() => setExpandedId(expanded ? null : frame.id)}
                  accessibilityRole="button"
                  accessibilityLabel={`OCPP frame ${frame.action}`}
                  style={({ pressed }) => [styles.frameHeader, pressed && { opacity: 0.85 }]}
                >
                  <OcppStatusPill
                    label={frame.direction.toUpperCase()}
                    color={directionColor(frame.direction)}
                    size="sm"
                  />
                  <Text style={styles.frameAction}>{frame.action}</Text>
                  <Text style={styles.frameLatency}>
                    {frame.latency_ms !== null ? `${frame.latency_ms}ms` : '—'}
                  </Text>
                </Pressable>
                <Text style={styles.frameTime}>{formatOcppTimestamp(frame.occurred_at)}</Text>
                {expanded ? (
                  <View style={{ marginTop: 8, gap: 8 }}>
                    <Text style={styles.jsonLabel}>payload</Text>
                    <ScrollView horizontal>
                      <OcppJsonViewer value={frame.payload} maxHeight={180} />
                    </ScrollView>
                    <Text style={styles.jsonLabel}>response</Text>
                    <ScrollView horizontal>
                      <OcppJsonViewer value={frame.response} maxHeight={180} />
                    </ScrollView>
                    {frame.error_code ? (
                      <Text style={styles.errorCode}>error: {frame.error_code}</Text>
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })}
        </OcppSectionCard>
      )}
    </View>
  );
};

export default RawFramesTab;

const styles = StyleSheet.create({
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  filterLabel: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    fontWeight: '600',
  },
  filterChips: {
    flexDirection: 'row',
    gap: 6,
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: OcppTheme.surfaceMuted,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  chipActive: {
    backgroundColor: OcppTheme.accentDim,
    borderColor: OcppTheme.accent,
  },
  chipText: {
    color: OcppTheme.textMuted,
    fontSize: 11,
    fontWeight: '600',
  },
  chipTextActive: {
    color: OcppTheme.accent,
  },
  frameRow: {
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  frameHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  frameAction: {
    flex: 1,
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '700',
  },
  frameLatency: {
    color: OcppTheme.textMuted,
    fontSize: 11,
  },
  frameTime: {
    color: OcppTheme.textDim,
    fontSize: 11,
    marginTop: 2,
  },
  jsonLabel: {
    color: OcppTheme.textMuted,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  errorCode: {
    color: OcppTheme.danger,
    fontSize: 12,
    fontWeight: '700',
  },
});
